import { useRef } from 'react';
import { useWheelNavigation } from '../hooks/useWheelNavigation';
import { useStore } from '../store/useStore';
import { useLiveQuery } from 'dexie-react-hooks';
import { AccountingEngine } from '../services/accountingEngine';

export const ProfitLossView = ({ config, handleItemSelect, selectedIndex, setSelectedIndex }: any) => {
  const scrollRef = useRef(null);
  const activeCompany = useStore((state) => state.activeCompany);
  const fromDate = useStore((state) => state.fromDate);
  const toDate = useStore((state) => state.toDate);
  
  const plData = useLiveQuery(async () => {
    if (!activeCompany) return null;
    return await AccountingEngine.generateProfitLoss(activeCompany.id!, fromDate, toDate);
  }, [activeCompany, fromDate, toDate]);

  const leftItems = plData?.expenses || [];
  const rightItems = plData?.incomes || [];
  const flatItems = [...leftItems, ...rightItems];

  useWheelNavigation(scrollRef, flatItems.length, setSelectedIndex, false, () => flatItems && flatItems[selectedIndex] && handleItemSelect(flatItems[selectedIndex], selectedIndex));

  if (!plData) {
    return (
      <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-black justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e8585]"></div>
        <div className="mt-4 font-bold text-gray-500">Calculating Profit & Loss...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[12px]">
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm z-10">
        <span className="w-1/3 text-left">Profit & Loss A/c</span>
        <span className="w-1/3 text-center">{activeCompany?.name || config.companyList[0].name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>

      <div className="bg-white text-black flex justify-between px-2 py-1 font-bold border-b border-[#aebec8] z-10 shadow-sm">
        <span>Profit & Loss A/c</span><span>{fromDate} to {toDate}</span>
      </div>

      <div className="flex flex-1 overflow-hidden bg-white">
        {/* Expenses (Left Side) */}
        <div className="flex-1 border-r border-[#aebec8] flex flex-col">
          <div className="flex justify-between font-bold border-b border-[#aebec8] px-2 py-1 bg-[#f9fafb]">
            <span>Particulars</span>
            <div className="flex gap-4">
              <span className="w-[100px] text-right"></span>
              <span className="w-[100px] text-right"></span>
            </div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-2">
            {leftItems.map((item: any, idx: number) => {
              const isSelected = selectedIndex === idx;
              return (
                <div key={idx} className={`flex justify-between py-1 cursor-pointer ${isSelected ? 'bg-[#1e8585] text-white font-bold active-row' : 'text-black font-semibold'}`} onClick={() => handleItemSelect(item, idx)}>
                  <span>{item.name}</span>
                  <span>{item.amount.toFixed(2)}</span>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between font-bold border-t border-b border-[#aebec8] px-2 py-1 text-black">
            <span>Total</span>
            <span>{plData?.totalExpenses.toFixed(2) || '0.00'}</span>
          </div>
        </div>

        {/* Incomes (Right Side) */}
        <div className="flex-1 flex flex-col">
          <div className="flex justify-between font-bold border-b border-[#aebec8] px-2 py-1 bg-[#f9fafb] text-black">
            <span>Particulars</span>
            <div className="flex gap-4">
              <span className="w-[100px] text-right"></span>
              <span className="w-[100px] text-right"></span>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {rightItems.map((item: any, idx: number) => {
              const globalIdx = leftItems.length + idx;
              const isSelected = selectedIndex === globalIdx;
              return (
                <div key={globalIdx} className={`flex justify-between py-1 cursor-pointer ${isSelected ? 'bg-[#1e8585] text-white font-bold active-row' : 'text-black font-semibold'}`} onClick={() => handleItemSelect(item, globalIdx)}>
                  <span>{item.name}</span>
                  <span>{item.amount.toFixed(2)}</span>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between font-bold border-t border-b border-[#aebec8] px-2 py-1 text-black">
            <span>Total</span>
            <span>{plData?.totalIncomes.toFixed(2) || '0.00'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
